import TokenAvatar from '@components/TokenAvatar'
import Touchable from '@components/Touchable'
import { LiquidiumMarket, LiquidiumReserve } from '@plug-core/providers/LiquidiumProvider'
import { formatMoney, formatNumber } from '@plug-core/utils/formatNumber'
import { Button, Surface, Text } from '@ui'
import React from 'react'
import { useTranslation } from 'react-i18next'

type PositionsMode = 'supply' | 'borrow'

const getInfo = (mode: PositionsMode) => {
  switch (mode) {
    case 'supply':
      return {
        leftButtonTitle: 'Lend more',
        rightButtonTitle: 'Withdraw',
      }
    case 'borrow':
    default:
      return {
        leftButtonTitle: 'Borrow more',
        rightButtonTitle: 'Repay',
      }
  }
}

interface PositionsProps {
  reserves: LiquidiumReserve[]
  markets: LiquidiumMarket[]
  mode: PositionsMode
  onPositionClick: (asset: string) => void
  onLeftButton: () => void
  onRightButton: () => void
}

const Positions: React.FC<PositionsProps> = ({
  reserves,
  markets,
  mode,
  onPositionClick,
  onLeftButton,
  onRightButton,
}) => {
  const { t } = useTranslation()

  const { leftButtonTitle, rightButtonTitle } = getInfo(mode)

  const positions = reserves.filter((reserve) =>
    mode === 'supply' ? reserve.supplied > 0 : reserve.borrowed > 0
  )

  return (
    <div>
      <Surface padding="none" className="p-5">
        {positions.length === 0 ? (
          <div className="mb-4 flex flex-col items-center gap-3">
            <Text tone="fg-2">{t('borrowLend.positions.noPositions')}</Text>
          </div>
        ) : (
          positions.map(({ reserve, token, supplied, borrowed, suppliedUsd, borrowedUsd }) => {
            const market = markets.find((m) => m.pool.id === reserve.pool.id)
            const rate = mode === 'supply' ? market?.supplyRate : market?.borrowRate

            return (
              <Touchable
                key={`${mode}-${reserve.pool.id}`}
                onClick={() => onPositionClick(reserve.pool.asset)}
              >
                <div className="mb-4 flex items-center gap-3">
                  <TokenAvatar logo={token?.logo} />

                  <div className="min-w-0 flex-1">
                    <Text weight="semibold" truncate>
                      {token?.symbol ?? reserve.pool.asset}
                    </Text>
                    <Text variant="body-s" tone="fg-2">
                      {mode == 'borrow' ? `APR` : 'APY'} {(rate ?? 0).toFixed(2)}%
                    </Text>
                  </div>

                  <div className="flex flex-col items-end">
                    <Text weight="semibold" tabular>
                      {formatNumber(mode === 'supply' ? supplied : borrowed)}
                    </Text>
                    <Text variant="body-s" tone="fg-2" tabular>
                      {formatMoney(mode === 'supply' ? suppliedUsd : borrowedUsd)}
                    </Text>
                  </div>
                </div>
              </Touchable>
            )
          })
        )}

        <div className="flex gap-6">
          <Button variant="secondary" fullWidth className="flex-1" onClick={onLeftButton}>
            {leftButtonTitle}
          </Button>
          <Button
            variant="secondary"
            fullWidth
            className="flex-1"
            disabled={positions.length == 0}
            onClick={onRightButton}
          >
            {rightButtonTitle}
          </Button>
        </div>
      </Surface>
    </div>
  )
}

export default Positions
